import React, { useState } from 'react';
import { CheckIcon, SendIcon } from 'lucide-react';
type Status = 'clean-strong' | 'clean-struggling' | 'setback' | null;
interface CheckInScreenProps {
  onSubmit: (achievements: string[], status: Status) => void;
}
interface AchievementItem {
  id: string;
  emoji: string;
  label: string;
}
const ACHIEVEMENTS: AchievementItem[] = [{
  id: 'salah',
  emoji: '☪️',
  label: 'Prayed all 5 salah'
}, {
  id: 'quran',
  emoji: '📖',
  label: 'Read Quran'
}, {
  id: 'exercise',
  emoji: '💪',
  label: 'Exercised'
}, {
  id: 'work',
  emoji: '📚',
  label: 'Worked/studied'
}, {
  id: 'dhikr',
  emoji: '📿',
  label: 'Made dhikr'
}, {
  id: 'helped',
  emoji: '🤝',
  label: 'Helped someone'
}, {
  id: 'outside',
  emoji: '🌳',
  label: 'Went outside'
}, {
  id: 'ate-healthy',
  emoji: '🍎',
  label: 'Ate healthy'
}, {
  id: 'sleep',
  emoji: '😴',
  label: 'Slept on time'
}];
interface StatusOption {
  id: Status;
  emoji: string;
  label: string;
}
const STATUS_OPTIONS: StatusOption[] = [{
  id: 'clean-strong',
  emoji: '💚',
  label: 'Clean and strong'
}, {
  id: 'clean-struggling',
  emoji: '💛',
  label: 'Clean but struggling'
}, {
  id: 'setback',
  emoji: '🔄',
  label: 'Had a setback'
}];
export function CheckInScreen({
  onSubmit
}: CheckInScreenProps) {
  const [selectedAchievements, setSelectedAchievements] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Status>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  function toggleAchievement(id: string) {
    setSelectedAchievements(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  }
  function handleSubmit() {
    if (!selectedStatus) return;
    onSubmit(selectedAchievements, selectedStatus);
    setIsSubmitted(true);
  }
  if (isSubmitted) {
    return <main className="flex flex-col items-center justify-center px-5 pt-20 pb-32 min-h-screen" data-id="element-45">
        <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mb-5" data-id="element-46">
          <CheckIcon className="w-8 h-8 text-success" data-id="element-47" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2" data-id="element-48">
          Check-in submitted
        </h2>
        <p className="text-base text-muted-foreground text-center max-w-xs" data-id="element-49">
          JazakAllahu khairan, brother. Keep going — every good deed counts.
        </p>
      </main>;
  }
  return <main className="flex flex-col px-5 pt-10 pb-32 min-h-screen" data-id="element-50">
      {/* Header */}
      <div className="mb-6" data-id="element-51">
        <h1 className="text-2xl font-bold text-foreground mb-2" data-id="element-52">
          How's your day going, brother?
        </h1>
        <p className="text-base text-muted-foreground" data-id="element-53">
          Share what you've accomplished. Every good deed matters.
        </p>
      </div>

      {/* Achievements section */}
      <section className="mb-8" aria-label="Today's achievements" data-id="element-54">
        <h2 className="text-sm text-muted-foreground uppercase tracking-wider font-medium mb-3" data-id="element-55">
          What did you accomplish today?
        </h2>
        <div className="grid grid-cols-2 gap-2.5" data-id="element-56">
          {ACHIEVEMENTS.map(item => {
          const isSelected = selectedAchievements.includes(item.id);
          return <button key={item.id} onClick={() => toggleAchievement(item.id)} className={`relative rounded-lg p-3.5 min-h-[56px] text-left transition-all duration-150 border flex items-center gap-2.5 ${isSelected ? 'bg-highlight/30 border-primary shadow-sm' : 'bg-card border-border hover:border-primary/30'}`} aria-pressed={isSelected} aria-label={item.label} data-id="element-57">
                {isSelected && <div className="absolute top-2 right-2" data-id="element-58">
                    <CheckIcon className="w-3.5 h-3.5 text-primary" data-id="element-59" />
                  </div>}
                <span className="text-lg flex-shrink-0" role="img" aria-hidden="true" data-id="element-60">
                  {item.emoji}
                </span>
                <span className="text-sm font-medium text-foreground leading-tight" data-id="element-61">
                  {item.label}
                </span>
              </button>;
        })}
        </div>
      </section>

      {/* Status section */}
      <section className="mb-8" aria-label="How are you doing" data-id="element-62">
        <h2 className="text-sm text-muted-foreground uppercase tracking-wider font-medium mb-3" data-id="element-63">
          How are you doing?
        </h2>
        <div className="flex flex-col gap-2.5" data-id="element-64">
          {STATUS_OPTIONS.map(option => {
          const isSelected = selectedStatus === option.id;
          return <button key={option.id} onClick={() => setSelectedStatus(option.id)} className={`rounded-lg p-4 min-h-[52px] text-left transition-all duration-150 border flex items-center gap-3 ${isSelected ? 'bg-highlight/30 border-primary shadow-sm' : 'bg-card border-border hover:border-primary/30'}`} role="radio" aria-checked={isSelected} aria-label={option.label} data-id="element-65">
                <span className="text-xl flex-shrink-0" role="img" aria-hidden="true" data-id="element-66">
                  {option.emoji}
                </span>
                <span className="text-base font-medium text-foreground" data-id="element-67">
                  {option.label}
                </span>
              </button>;
        })}
        </div>

        {/* Mercy message for setback */}
        {selectedStatus === 'setback' && <div className="mt-3 bg-card rounded-lg border border-border p-4" data-id="element-68">
            <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-69">
              It's okay, brother. Tawbah is always open. Allah's mercy is
              greater than any slip. What matters is you're here, trying again.
            </p>
          </div>}
      </section>

      {/* Submit button */}
      <button onClick={handleSubmit} disabled={!selectedStatus} className={`w-full font-semibold text-base rounded-lg py-4 px-6 min-h-[52px] transition-all duration-150 flex items-center justify-center gap-2 ${selectedStatus ? 'bg-primary text-primary-foreground shadow-md hover:shadow-lg active:scale-[0.98]' : 'bg-muted text-muted-foreground cursor-not-allowed'}`} aria-label="Submit check-in" data-id="element-70">
        <SendIcon className="w-5 h-5" data-id="element-71" />
        Submit check-in
      </button>
    </main>;
}